import cx from "classnames";
import React, { memo, useCallback, useEffect, useMemo, useState, type ComponentProps } from "react";
import { useProject, type Project } from "../../../../../../api/useProjects";
import useModal from "../../../../../../ui/lib/hooks/useModal";
import { useDashboardEditorContext } from "../../../hook/context/editor";
import { useDashboardEditorState } from "../../../hook/store/editor";
import { type DashboardBlockModelT } from "../../../schema/dashboard";
import { printableBlockContainer } from "../../../utils/print";
import { Block } from "./Block";
import { AddBlock } from "./Block/AddBlock";
import {
  consolidate,
  CopyGridItem,
  Grid,
  GridItemAction,
  GridItemActionSeparator,
  InsertGridItem,
  MoveGridItem,
  RemoveGridItem,
  type LayoutItem,
} from "./Grid";
import t from "../../../../../../i18n";
import { useTranslations } from "../../../../../../api/useTranslations";
import { useUser } from "../../../../../../api/useUser";
import { UploadLine } from "../../../../../../ui/IconV2/react";
import Button from "../../../../../../ui/Button";
import { DashboardImportModal } from "../../../component/DashboardImportModal";
import { colors, spacings } from "../../../../../../ui/Theme";
import { css } from "@linaria/core";
import { Text } from "../../../../../../ui/Text";
import { useDashboard } from "../../../hook/api/dashboard";
import type { BlockModelT } from "../../../schema/block";

export const DashboardContent = ({
  editable = false,
  projectId,
  dashboardId,
  powerUnitId,
}: {
  editable?: boolean;
  projectId: string;
  dashboardId: string;
  powerUnitId?: string | null;
}) => {
  const project = useProject(projectId);
  const { blocks: dashboardBlocks } = useDashboard(projectId, dashboardId);
  const editor = useDashboardEditorContext();
  const editorState = useDashboardEditorState(editor?.store);
  const blocks = useMemo(
    () => (editable ? editorState?.blocks : dashboardBlocks) ?? [],
    [editable, editorState?.blocks, dashboardBlocks],
  );
  const blocksLayoutKey = blocks.map(block => `${block.id}:${block.position.join(",")}`).join("|");

  const computeLayout = useCallback((blocks: DashboardBlockModelT[]): LayoutItem[] => {
    if (blocks.length === 0) {
      return [{ id: crypto.randomUUID(), x: 1, y: 1, w: 1, h: 1 }];
    }

    const newLayout = blocks.map(block => ({
      id: block.id,
      x: block.position[0],
      y: block.position[1],
      w: block.position[2],
      h: block.position[3],
    }));

    const lastMatchBlock = blocks.some(block => {
      const [x, y] = block.position;
      const bottomLayoutItem = newLayout[newLayout.length - 1];
      return x === bottomLayoutItem?.x && y === bottomLayoutItem?.y;
    });

    if (lastMatchBlock) {
      const y = Math.max(...newLayout.map(({ y }) => y)) + 1;
      const w = Math.max(...newLayout.map(({ w }) => w));
      newLayout.push({ id: crypto.randomUUID(), x: 1, y, w, h: 1 });
    }

    return consolidate(newLayout);
  }, []);

  const onLayoutChange = useCallback(
    (newLayout: LayoutItem[]) => {
      const updatedBlocks = newLayout
        .map(({ id, x, y, w, h }) => {
          const block = blocks.find(block => block.id === id);
          if (!block) return null;
          return { ...block, position: [x, y, w, h] };
        })
        .filter((block): block is DashboardBlockModelT => Boolean(block));

      void editor?.updateManyBlocks(updatedBlocks);
      editor?.focus(null);

      setLayout(() => {
        if (newLayout.length === 0) {
          return [{ id: crypto.randomUUID(), x: 1, y: 1, w: 1, h: 1 }];
        }

        const lastMatchBlock = updatedBlocks.some(block => {
          const [x, y] = block.position;
          const bottomLayoutItem = newLayout[newLayout.length - 1];
          return x === bottomLayoutItem?.x && y === bottomLayoutItem?.y;
        });

        if (lastMatchBlock) {
          const y = Math.max(...newLayout.map(({ y }) => y)) + 1;
          const w = Math.max(...newLayout.map(({ w }) => w));
          return [...newLayout, { id: crypto.randomUUID(), x: 1, y, w, h: 1 }];
        }

        return newLayout;
      });
    },
    [blocks, editor],
  );

  const onBlockAdded = useCallback((block: BlockModelT) => {
    setLayout(layout => {
      if (layout.length === 0) {
        return [{ id: crypto.randomUUID(), x: 1, y: 1, w: 1, h: 1 }];
      }

      const [x, y] = block.position;
      const bottomLayoutItem = layout[layout.length - 1];
      const lastMatchBlock = x === bottomLayoutItem?.x && y === bottomLayoutItem?.y;

      if (lastMatchBlock) {
        const y = Math.max(...layout.map(({ y }) => y)) + 1;
        const w = Math.max(...layout.map(({ w }) => w));
        return consolidate([...layout, { id: crypto.randomUUID(), x: 1, y, w, h: 1 }]);
      }
      return layout;
    });
  }, []);

  const [layout, setLayout] = useState<LayoutItem[]>(() => computeLayout(blocks));

  /**
   * @note In the current implementation, block IDs change on each update,
   * so we need to subscribe to all changes in order to recompute the layout.
   * In addition, we will align this behavior with local changes,
   * even if the IDs are stable.
   */
  useEffect(() => {
    setLayout(computeLayout(blocks));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocksLayoutKey, computeLayout]);

  if (!project) return null;

  return (
    <div>
      <Grid layout={layout} onLayoutChange={editable ? onLayoutChange : undefined}>
        {item => (
          <>
            {editable && editor && (
              <GridItemAction item={item}>
                <MoveGridItem source={item} direction="left" />
                <MoveGridItem source={item} direction="right" />
                <MoveGridItem source={item} direction="up" />
                <MoveGridItem source={item} direction="down" />
                <GridItemActionSeparator />
                <InsertGridItem source={item} direction="left" />
                <InsertGridItem source={item} direction="right" />
                <InsertGridItem source={item} direction="up" />
                <InsertGridItem source={item} direction="down" />
                <GridItemActionSeparator />
                <CopyGridItem
                  source={item}
                  onCopyLayoutItem={(toLayoutItem, fromLayoutItem) => {
                    const block = blocks.find(block => block.id === fromLayoutItem.id);
                    if (!block) return;

                    return editor.createBlock({
                      ...block,
                      id: toLayoutItem.id,
                      timestamp: Date.now(),
                      position: [toLayoutItem.x, toLayoutItem.y, toLayoutItem.w, toLayoutItem.h],
                    });
                  }}
                />
                <RemoveGridItem
                  source={item}
                  onRemoveLayoutItem={layoutItem => {
                    const block = blocks.find(block => block.id === layoutItem.id);
                    if (!block) return;
                    return editor.deleteBlock(block);
                  }}
                />
              </GridItemAction>
            )}
            <GridItemBlock
              dashboardId={editor?.dashboardId}
              project={project}
              item={item}
              block={blocks.find(block => block.id === item.id)}
              editable={editable}
              powerUnitId={powerUnitId}
              onBlockAdded={onBlockAdded}
            />
          </>
        )}
      </Grid>
      {editable && editor && blocks.length === 0 && (
        <DashboardContentEditPlaceholder dashboardId={editor.dashboardId} projectId={project.id} />
      )}
    </div>
  );
};

const GridItemBlock = memo(
  ({
    block,
    dashboardId,
    editable,
    item,
    project,
    powerUnitId,
    onBlockAdded,
  }: {
    block?: DashboardBlockModelT;
    dashboardId?: string;
    editable: boolean;
    item: LayoutItem;
    project: Project;
    powerUnitId?: string | null;
    onBlockAdded?: ComponentProps<typeof AddBlock>["onBlockAdded"];
  }) => {
    if (!block && editable && dashboardId) {
      return (
        <AddBlock
          dashboardId={dashboardId}
          blockId={item.id}
          blockPosition={[item.x, item.y, item.w, item.h]}
          onBlockAdded={onBlockAdded}
        />
      );
    }

    if (!block) return null;

    return (
      <div
        className={cx(
          block.kind,
          block.kind === "HEADING" ? `${block.kind}_${block.config.level}` : "",
          printableBlockContainer,
        )}
      >
        <Block project={project} block={block} editable={editable} powerUnitId={powerUnitId} />
      </div>
    );
  },
);

const DashboardContentEditPlaceholder = ({
  projectId,
  dashboardId,
}: {
  projectId: string;
  dashboardId: string;
}) => {
  const user = useUser();
  const translations = useTranslations();
  const { modal, openModal, closeModal } = useModal();
  return (
    <div className={contentEditPlaceholder}>
      <Text type="body1SemiBold">{t("project.dashboard.placeholder.or")}</Text>
      <Text type="body1">{t("project.dashboard.placeholder.text", translations)}</Text>
      <Button variant="primary" leftIcon={<UploadLine />} onClick={openModal}>
        {t("project.dashboards.import.action.validation")}
      </Button>
      <DashboardImportModal
        projectId={projectId}
        dashboardId={dashboardId}
        userId={user.id}
        opened={modal}
        onClose={closeModal}
        canBePublished={false}
      />
    </div>
  );
};

const contentEditPlaceholder = css`
  grid-column: 1 / span 1;
  grid-row: 2 / span 1;
  text-align: center;
  margin-top: ${spacings.xs};
  color: ${colors.carbon800};

  > * + * {
    margin-top: ${spacings.m};
  }
`;
